import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "font-medium text-sm" }
const _hoisted_2 = {
  key: 0,
  class: "superset-assistant__model",
  "data-testid": "superset.assistant-model"
}
const _hoisted_3 = { class: "sps-card-content py-2 px-3 superset-assistant__body" }
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = { class: "superset-assistant__content" }
const _hoisted_6 = {
  key: 0,
  class: "superset-assistant__actions"
}
const _hoisted_7 = {
  key: 0,
  class: "superset-assistant__hint",
  "data-testid": "superset.assistant-busy"
}
const _hoisted_8 = { class: "flex gap-1 pt-2 shrink-0" }
const _hoisted_9 = ["disabled"]

import { nextTick, ref } from 'vue';

import { SupersetStore } from 'Pages/BI/Stores/SupersetStore';

// Tools that CHANGE something; everything else (list_*, get_*) is a question
// whose answer must leave the user's screen exactly where it was.
interface AssistantTurn {
    Role: string;
    Content: string;
    IsUser: boolean;
    Actions: string[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SupersetAssistant',
  props: {
    model: { default: '' },
    currentDashboardId: { default: '' },
    context: { default: () => ({ kind: '', id: '', label: '' }) }
  },
  emits: ["close", "open-dashboard", "open-path", "refresh"],
  setup(__props: any, { emit: __emit }) {

const MUTATING_TOOLS = [
    'generate_chart',
    'generate_dashboard',
    'update_chart',
    'update_chart_preview',
    'create_virtual_dataset',
    'add_chart_to_existing_dashboard',
    'save_sql_query',
];

const props = __props;

const emit = __emit;

const turns = ref<AssistantTurn[]>([]);
const input = ref('');
const busy = ref(false);
const left = ref<number | null>(null);
const top = ref<number | null>(null);
const panel = ref<HTMLElement | null>(null);
const log = ref<HTMLElement | null>(null);

const scrollToEnd = () => {
    nextTick(() => {
        if (log.value) {
            log.value.scrollTop = log.value.scrollHeight;
        }
    });
};

const send = () => {
    const message = (input.value || '').trim();
    if (!message || busy.value) {
        return;
    }

    const history = turns.value.map(turn => ({ Role: turn.Role, Content: turn.Content }));
    turns.value.push({ Role: 'user', Content: message, IsUser: true, Actions: [] });
    input.value = '';
    busy.value = true;
    scrollToEnd();

    SupersetStore.Ask(message, history, props.currentDashboardId, {
        Kind: props.context?.kind || '',
        Id: props.context?.id || '',
        Label: props.context?.label || '',
    })
        .always(() => {
            busy.value = false;
            scrollToEnd();
        })
        .fail((error: any) => {
            turns.value.push({ Role: 'assistant', Content: error.message, IsUser: false, Actions: [] });
        })
        .then((reply: any) => {
            turns.value.push({
                Role: 'assistant',
                Content: reply.Reply,
                IsUser: false,
                Actions: reply.Actions || [],
            });

            // The assistant drives the designer: it names the dashboard to show,
            // otherwise a build refreshes whatever is already open.
            if (reply.DashboardId) {
                emit('open-dashboard', reply.DashboardId);
            } else if (reply.NavigatePath) {
                emit('open-path', reply.NavigatePath);
            } else if ((reply.Actions || []).some((action: string) => MUTATING_TOOLS.includes(action))) {
                emit('refresh');
            }
        });
};

// The model is given the whole conversation on every ask, so a stale thread
// steers later answers - and costs tokens. Starting fresh drops all of it.
const startNewChat = () => {
    turns.value = [];
    input.value = '';
    busy.value = false;
};

const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
    }
};

const startDrag = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest('a, i, input')) {
        return;
    }

    const box = panel.value?.getBoundingClientRect();
    if (!box) {
        return;
    }

    const offsetX = event.clientX - box.left;
    const offsetY = event.clientY - box.top;

    const move = (moveEvent: MouseEvent) => {
        left.value = Math.min(Math.max(0, moveEvent.clientX - offsetX), window.innerWidth - box.width);
        top.value = Math.min(Math.max(0, moveEvent.clientY - offsetY), window.innerHeight - 40);
    };
    const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
    };

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "panel",
    ref: panel,
    class: "sps-card superset-assistant-float",
    "data-testid": "superset.assistant",
    style: _normalizeStyle({
             left: left.value === null ? 'auto' : left.value + 'px',
             top: top.value === null ? 'auto' : top.value + 'px',
             right: left.value === null ? '24px' : 'auto',
             bottom: top.value === null ? '24px' : 'auto',
         })
  }, [
    _createElementVNode("div", {
      class: "flex items-center justify-between px-3 py-2 superset-assistant__header",
      "data-testid": "superset.assistant-header",
      onMousedown: startDrag
    }, [
      _createElementVNode("span", _hoisted_1, [
        _cache[2] || (_cache[2] = _createTextVNode(" Analytics assistant ", -1 /* CACHED */)),
        (__props.model)
          ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(__props.model), 1 /* TEXT */))
          : _createCommentVNode("v-if", true)
      ]),
      _createElementVNode("a", {
        class: "sps-btn sps-btn-ghost",
        title: "New chat",
        "data-testid": "superset.assistant-new",
        onClick: startNewChat
      }, [...(_cache[3] || (_cache[3] = [
        _createElementVNode("i", { class: "fa fa-plus" }, null, -1 /* CACHED */)
      ]))]),
      _createElementVNode("a", {
        class: "sps-btn sps-btn-ghost",
        "data-testid": "superset.assistant-close",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      }, [...(_cache[4] || (_cache[4] = [
        _createElementVNode("i", { class: "fa fa-times" }, null, -1 /* CACHED */)
      ]))])
    ], 32 /* NEED_HYDRATION */),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        ref_key: "log",
        ref: log,
        class: "superset-assistant__log",
        "data-testid": "superset.assistant-log"
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(turns.value, (turn, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: _normalizeClass(["superset-assistant__turn", turn.IsUser ? 'superset-assistant__turn--user' : 'superset-assistant__turn--assistant']),
            "data-testid": turn.IsUser ? 'superset.assistant-user' : 'superset.assistant-reply'
          }, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(turn.Content), 1 /* TEXT */),
            (turn.Actions && turn.Actions.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(turn.Actions.join(', ')), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ], 10 /* CLASS, PROPS */, _hoisted_4))
        }), 128 /* KEYED_FRAGMENT */)),
        (busy.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, "Working..."))
          : _createCommentVNode("v-if", true)
      ], 512 /* NEED_PATCH */),
      _createElementVNode("div", _hoisted_8, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((input).value = $event)),
          type: "text",
          class: "sps-input grow",
          placeholder: "Ask the assistant",
          "data-testid": "superset.assistant-input",
          disabled: busy.value,
          onKeydown: onKeyDown
        }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_9), [
          [_vModelText, input.value]
        ]),
        _createElementVNode("a", {
          class: "sps-btn sps-btn-primary",
          "data-testid": "superset.assistant-send",
          onClick: send
        }, [...(_cache[5] || (_cache[5] = [
          _createElementVNode("i", { class: "fa fa-paper-plane" }, null, -1 /* CACHED */)
        ]))])
      ])
    ])
  ], 4 /* STYLE */))
}
}

})